import type {
  MfgRecipeDetailResponse,
  MfgRecipeKind,
  MfgRecipeLineRole,
} from "@/lib/manufacturing/types";

export type DuplicateRecipeLineKind = "material" | "component" | "use";

export type DuplicateRecipeLineDraft = {
  line_kind: DuplicateRecipeLineKind;
  material_id: string;
  component_recipe_id: string;
  material_use_id: string;
  quantity: number;
  role: MfgRecipeLineRole;
  slot_label: string;
  upgrade_price_delta: number | null;
  swappable: boolean;
  /** Display-only labels for the new-model preview. */
  material_code?: string;
  material_name?: string;
  component_recipe_code?: string;
  component_recipe_name?: string;
  material_use_code?: string;
  material_use_name?: string;
};

export type DuplicateRecipeDraft = {
  sourceCode: string;
  sourceName: string;
  kind: MfgRecipeKind;
  tags: string[];
  notes: string;
  lines: DuplicateRecipeLineDraft[];
};

function duplicateLineKindFromDetail(
  ln: MfgRecipeDetailResponse["lines"][number],
): DuplicateRecipeLineKind {
  if (ln.line_kind === "use" || ln.material_use_id) return "use";
  if (ln.line_kind === "component" || ln.component_recipe_id) return "component";
  return "material";
}

export function duplicateLinePreviewLabel(ln: DuplicateRecipeLineDraft): string {
  if (ln.line_kind === "use") {
    const code = ln.material_use_code?.trim() || "Part use";
    const name = ln.material_use_name?.trim() || ln.material_use_id;
    return `${code} — ${name}`;
  }
  if (ln.line_kind === "component") {
    const code = ln.component_recipe_code?.trim() || "Sub-recipe";
    const name = ln.component_recipe_name?.trim() || ln.component_recipe_id;
    return `${code} — ${name}`;
  }
  const code = ln.material_code?.trim() || "Material";
  const name = ln.material_name?.trim() || ln.material_id;
  return `${code} — ${name}`;
}

export function duplicateRecipeDraftFromDetail(
  detail: MfgRecipeDetailResponse,
): DuplicateRecipeDraft {
  return {
    sourceCode: detail.recipe.code,
    sourceName: detail.recipe.name,
    kind: detail.recipe.kind ?? "assembly",
    tags: Array.isArray(detail.recipe.tags) ? [...detail.recipe.tags] : [],
    notes: detail.recipe.notes ?? "",
    lines: detail.lines.map((ln) => ({
      line_kind: duplicateLineKindFromDetail(ln),
      material_id: ln.material_id ?? "",
      component_recipe_id: ln.component_recipe_id ?? "",
      material_use_id: ln.material_use_id ?? "",
      quantity: ln.quantity,
      role: ln.role,
      slot_label: ln.slot_label ?? "",
      upgrade_price_delta:
        typeof ln.upgrade_price_delta === "number" && Number.isFinite(ln.upgrade_price_delta)
          ? ln.upgrade_price_delta
          : null,
      swappable: ln.swappable,
      material_code: ln.material_code || undefined,
      material_name: ln.material_name || undefined,
      component_recipe_code: ln.component_recipe_code || undefined,
      component_recipe_name: ln.component_recipe_name || undefined,
      material_use_code: ln.material_use_code || undefined,
      material_use_name: ln.material_use_name || undefined,
    })),
  };
}

export function duplicateLinesToSetPayload(lines: readonly DuplicateRecipeLineDraft[]) {
  const out: Array<{
    material_id?: string;
    component_recipe_id?: string;
    material_use_id?: string;
    quantity: number;
    sort_order: number;
    role: MfgRecipeLineRole;
    slot_label: string;
    upgrade_price_delta: number | null;
    swappable: boolean;
  }> = [];
  let sort = 0;
  for (const row of lines) {
    if (!Number.isFinite(row.quantity) || row.quantity === 0) continue;
    sort += 1;
    if (row.line_kind === "material") {
      const mid = row.material_id.trim();
      if (!mid) continue;
      out.push({
        material_id: mid,
        quantity: row.quantity,
        sort_order: sort,
        role: row.role,
        slot_label: row.slot_label.trim(),
        upgrade_price_delta: row.upgrade_price_delta,
        swappable: row.swappable,
      });
      continue;
    }
    if (row.line_kind === "use") {
      const uid = row.material_use_id.trim();
      if (!uid) continue;
      out.push({
        material_use_id: uid,
        quantity: row.quantity,
        sort_order: sort,
        role: row.role,
        slot_label: row.slot_label.trim(),
        upgrade_price_delta: row.upgrade_price_delta,
        swappable: row.swappable,
      });
      continue;
    }
    const cid = row.component_recipe_id.trim();
    if (!cid) continue;
    out.push({
      component_recipe_id: cid,
      quantity: row.quantity,
      sort_order: sort,
      role: row.role,
      slot_label: row.slot_label.trim(),
      upgrade_price_delta: row.upgrade_price_delta,
      swappable: row.swappable,
    });
  }
  return out;
}
